import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { addManualLedger } from '../firebase/database';
import { generateManualLedgerPDF } from '../services/manualLedgerPdfService';
import { downloadPDF, openPDFInNewTab } from '../services/invoicePdfService';
import { ManualLedger, ManualLedgerEntry } from '../types';
import toast from 'react-hot-toast';

const CreateLedger: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Refs for input fields
  const partyNameRef = useRef<HTMLInputElement>(null);
  const entryDateRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryParticularsRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryDebitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const entryCreditRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [partyName, setPartyName] = useState('');
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [logisticsCompany, setLogisticsCompany] = useState<'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.' | ''>('');
  const [entries, setEntries] = useState<ManualLedgerEntry[]>([
    {
      id: Date.now().toString(),
      date: new Date(),
      particulars: '',
      debit: 0,
      credit: 0,
      balance: 0
    }
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [showValidation, setShowValidation] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // Add new entry
  const addEntry = () => {
    const newEntry: ManualLedgerEntry = {
      id: Date.now().toString(),
      date: new Date(),
      particulars: '',
      debit: 0,
      credit: 0,
      balance: 0
    };
    setEntries([...entries, newEntry]);
  };

  // Remove entry
  const removeEntry = (id: string) => {
    if (entries.length <= 1) return; // Keep at least one entry
    setEntries(entries.filter(entry => entry.id !== id));
  };

  // Update entry
  const updateEntry = (id: string, field: keyof ManualLedgerEntry, value: any) => {
    setEntries(entries.map((entry, index) => {
      if (entry.id === id) {
        const updatedEntry = { ...entry, [field]: value };
        
        // Calculate balance based on debit and credit
        if (field === 'debit' || field === 'credit') {
          const debit = field === 'debit' ? value : entry.debit;
          const credit = field === 'credit' ? value : entry.credit;
          
          // Calculate running balance including opening balance
          // Debit subtracts from balance, Credit adds to balance
          let runningBalance = openingBalance;
          for (let i = 0; i < entries.length; i++) {
            if (i < index) {
              runningBalance += entries[i].credit - entries[i].debit;
            } else if (i === index) {
              runningBalance += credit - debit;
            }
          }
          updatedEntry.balance = runningBalance;
        }
        
        return updatedEntry;
      }
      return entry;
    }));
  };

  // Scroll to first empty field
  const scrollToFirstEmptyField = (errors: Record<string, string>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;

    // Priority order for scrolling to fields
    const fieldPriority = ['partyName'];

    // Find the first error field in priority order
    let targetField = null;
    for (const priorityField of fieldPriority) {
      if (errors[priorityField]) {
        targetField = priorityField;
        break;
      }
    }

    // If no priority field found, check for entry errors
    if (!targetField) {
      for (const errorKey of errorKeys) {
        if (errorKey.startsWith('entry_')) {
          targetField = errorKey;
          break;
        }
      }
    }

    // Scroll to the target field
    if (targetField) {
      let targetRef: React.RefObject<HTMLElement> | null = null;
      
      switch (targetField) {
        case 'partyName':
          targetRef = partyNameRef;
          break;
        default:
          // Handle entry errors
          if (targetField.startsWith('entry_')) {
            const parts = targetField.split('_');
            const index = parseInt(parts[1]);
            const field = parts[2];
            
            if (field === 'date' && entryDateRefs.current[index]) {
              targetRef = { current: entryDateRefs.current[index] };
            } else if (field === 'particulars' && entryParticularsRefs.current[index]) {
              targetRef = { current: entryParticularsRefs.current[index] };
            } else if (field === 'debit' && entryDebitRefs.current[index]) {
              targetRef = { current: entryDebitRefs.current[index] };
            } else if (field === 'credit' && entryCreditRefs.current[index]) {
              targetRef = { current: entryCreditRefs.current[index] };
            }
          }
          break;
      }

      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        targetRef.current.focus();
      }
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!partyName.trim()) errors['partyName'] = 'Party name is required';
    if (!logisticsCompany) errors['logisticsCompany'] = 'Logistics company is required';
    
    entries.forEach((entry, index) => {
      if (!entry.date) errors[`entry_${index}_date`] = 'Date is required';
      if (!entry.particulars.trim()) errors[`entry_${index}_particulars`] = 'Particulars is required';
      if (entry.debit < 0) errors[`entry_${index}_debit`] = 'Debit cannot be negative';
      if (entry.credit < 0) errors[`entry_${index}_credit`] = 'Credit cannot be negative';
    });
    
    setFieldErrors(errors);
    
    // Scroll to first empty field if validation fails
    if (Object.keys(errors).length > 0) {
      setTimeout(() => scrollToFirstEmptyField(errors), 100);
    }
    
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to save ledger.');
      return;
    }
    setError('');
    
    setIsSaving(true);
    try {
      const ledgerData = {
        partyName: partyName.trim(),
        companyName: partyName.trim(), // Use party name as company name
        logisticsCompany: (logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.'),
        openingBalance,
        entries: entries.map(entry => ({
          ...entry,
          particulars: entry.particulars.trim()
        })),
        createdBy: currentUser.id,
        // Add logo URL based on selected logistics company
        logoUrl: logisticsCompany === 'VECTOR LOGISTICS' 
          ? '/assets/vector_logo.png' 
          : '/assets/barq_logo-min.png'
      };

      await addManualLedger(ledgerData);
      toast.success('Ledger created successfully!');
      navigate('/manual-ledgers');
    } catch (e) {
      console.error('Save error:', e);
      setError(`Failed to save ledger: ${e instanceof Error ? e.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleViewPDF = async () => {
    if (!currentUser) return;
    
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to preview PDF.');
      return;
    }
    setError('');
    
    setIsGeneratingPDF(true);
    try {
      const ledgerData: ManualLedger = {
        id: 'preview',
        partyName: partyName.trim(),
        companyName: partyName.trim(),
        logisticsCompany: (logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.'),
        openingBalance,
        entries: entries.map(entry => ({
          ...entry,
          particulars: entry.particulars.trim()
        })),
        createdBy: currentUser.id,
        createdAt: new Date(),
        logoUrl: logisticsCompany === 'VECTOR LOGISTICS' 
          ? '/assets/vector_logo.png' 
          : '/assets/barq_logo-min.png'
      };

      const pdf = await generateManualLedgerPDF(ledgerData);
      openPDFInNewTab(pdf, 'Manual Ledger PDF');
    } catch (e) {
      console.error('PDF error:', e);
      setError('Error generating PDF preview');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!currentUser) return;
    
    setShowValidation(true);
    if (!validateForm()) {
      setError('Please fill all required fields to download PDF.');
      return;
    }
    setError('');
    
    setIsGeneratingPDF(true);
    try {
      const ledgerData: ManualLedger = {
        id: 'download',
        partyName: partyName.trim(),
        companyName: partyName.trim(),
        logisticsCompany: (logisticsCompany as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.'),
        openingBalance,
        entries: entries.map(entry => ({
          ...entry,
          particulars: entry.particulars.trim()
        })),
        createdBy: currentUser.id,
        createdAt: new Date(),
        logoUrl: logisticsCompany === 'VECTOR LOGISTICS' 
          ? '/assets/vector_logo.png' 
          : '/assets/barq_logo-min.png'
      };

      const pdf = await generateManualLedgerPDF(ledgerData);
      const filename = `ManualLedger_${partyName.trim()}_${new Date().toISOString().split('T')[0]}.pdf`;
      downloadPDF(pdf, filename);
    } catch (e) {
      console.error('PDF error:', e);
      setError('Error generating PDF download');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Create Ledger</h1>
          <button 
            onClick={() => navigate(-1)} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
          >
            Back
          </button>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Party Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Party Name *
              </label>
              <input
                ref={partyNameRef}
                type="text"
                value={partyName}
                onChange={(e) => setPartyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter party name"
              />
              {showValidation && fieldErrors['partyName'] && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors['partyName']}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">This will be used as both party name and company name</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opening Balance
              </label>
              <input
                type="number"
                step="0.01"
                value={openingBalance}
                onChange={(e) => setOpeningBalance(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="0.00"
              />
              <p className="mt-1 text-xs text-gray-500">Initial balance for this ledger</p>
            </div>
          </div>

          {/* Logistics Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logistics Company *
            </label>
            <select
              value={logisticsCompany}
              onChange={(e) => setLogisticsCompany(e.target.value as 'VECTOR LOGISTICS' | 'BARQ CARGO SYSTEM PVT LTD.' | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select a company</option>
              <option value="VECTOR LOGISTICS">VECTOR LOGISTICS</option>
              <option value="BARQ CARGO SYSTEM PVT LTD.">BARQ CARGO SYSTEM PVT LTD.</option>
            </select>
            {showValidation && fieldErrors['logisticsCompany'] && (
              <p className="mt-1 text-sm text-red-600">{fieldErrors['logisticsCompany']}</p>
            )}
          </div>

          {/* Ledger Entries */}
          <div>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-900">Ledger Entries</h3>
            </div>

            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Entry {index + 1}</h4>
                    {entries.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEntry(entry.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Date *</label>
                      <input
                        ref={el => entryDateRefs.current[index] = el}
                        type="date"
                        value={entry.date.toISOString().substring(0, 10)}
                        onChange={(e) => updateEntry(entry.id, 'date', new Date(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      />
                      {showValidation && fieldErrors[`entry_${index}_date`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_date`]}</p>
                      )}
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Particulars *</label>
                      <input
                        ref={el => entryParticularsRefs.current[index] = el}
                        type="text"
                        value={entry.particulars}
                        onChange={(e) => updateEntry(entry.id, 'particulars', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="Enter particulars"
                      />
                      {showValidation && fieldErrors[`entry_${index}_particulars`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_particulars`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Debit</label>
                      <input
                        ref={el => entryDebitRefs.current[index] = el}
                        type="number"
                        step="0.01"
                        min="0"
                        value={entry.debit}
                        onChange={(e) => updateEntry(entry.id, 'debit', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="0.00"
                      />
                      {showValidation && fieldErrors[`entry_${index}_debit`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_debit`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Credit</label>
                      <input
                        ref={el => entryCreditRefs.current[index] = el}
                        type="number"
                        step="0.01"
                        min="0"
                        value={entry.credit}
                        onChange={(e) => updateEntry(entry.id, 'credit', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                        placeholder="0.00"
                      />
                      {showValidation && fieldErrors[`entry_${index}_credit`] && (
                        <p className="mt-1 text-xs text-red-600">{fieldErrors[`entry_${index}_credit`]}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Balance:</span>
                      <span className={`text-sm font-bold ${entry.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {entry.balance >= 0 ? '+' : ''}{entry.balance.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add Entry Button - bottom right */}
            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={addEntry}
                className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors duration-200"
              >
                + Add Entry
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              onClick={handleViewPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
            >
              {isGeneratingPDF ? 'Generating...' : 'View PDF'}
            </button>
            <button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 disabled:opacity-50"
            >
              {isGeneratingPDF ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save Ledger'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLedger;
